import { APPS_LIMIT, AppGroupType, SortType } from '@wix/app-market-services';
import type {
  AppGroupSection,
  Services,
  QueryAppsFilter,
} from '@wix/app-market-services';
import { fetchWithQuery } from '@wix/app-market-core';
import type { QueryAppsResponse } from '../../components/apps-section';
import type { QueryClient } from '@tanstack/react-query';
import type Experiments from '@wix/wix-experiments';

export interface SubCategoryPageHeaderData {
  slug: string;
  parentSlug: string;
  parentName: string;
  primaryTitle: string;
}

export async function getSubCategoryComponentData({
  queryClient,
  slug,
  services,
  languageCode,
  isSale,
}: {
  queryClient: QueryClient;
  services: Services;
  slug: string;
  languageCode: string;
  isSale: boolean;
  experiments: Experiments;
}): Promise<QueryAppsResponse> {
  const subCategory = await getTag({
    queryClient,
    tagSlug: slug,
    services,
    languageCode,
  });

  const { appIds, hasMoreApps, totalApps } =
    await services.appMarketClientAggregator.enhancedSearchApps({
      sortTypes: [SortType.DEALER],
      paging: {
        limit: APPS_LIMIT,
        offset: 0,
      },
      filter: { isSale, tagId: subCategory.id },
    });

  const appsData = await services.getAppsByAppIds({
    appIds,
    status: 'PUBLISHED',
  });
  const apps = appsData.toJSON();

  return {
    appGroup: {
      type: AppGroupType.COLLECTION,
      tagSlug: slug,
      apps: apps as AppGroupSection['apps'],
      title: '',
      totalAppsCount: 0,
    },
    paging: {
      total: totalApps,
      hasNext: hasMoreApps,
    },
    collectionName: subCategory.name,
  };
}

export async function getSubCategoryHeaderPageData({
  queryClient,
  slug,
  parentSlug,
  services,
  languageCode,
}: {
  queryClient: QueryClient;
  services: Services;
  slug: string;
  parentSlug: string;
  languageCode: string;
}): Promise<SubCategoryPageHeaderData> {
  const [subCategory, category] = await Promise.all([
    getTag({ queryClient, tagSlug: slug, services, languageCode }),
    getTag({ queryClient, tagSlug: parentSlug, services, languageCode }),
  ]);

  return {
    slug,
    parentSlug,
    parentName: category.name,
    primaryTitle: subCategory.name,
  };
}

async function getTag({
  queryClient,
  tagSlug,
  services,
  languageCode,
}: {
  queryClient: QueryClient;
  services: Services;
  tagSlug: string;
  languageCode: string;
}) {
  const tags = await fetchWithQuery({
    queryClient,
    queryFn: () =>
      services.queryTag({ slugs: [tagSlug], languageCode, isHidden: false }),
    queryKey: `query-tag-${tagSlug}`,
  });

  const tag = tags.findBySlug(tagSlug);
  if (tag.id === '') {
    throw new Error(
      `tag with slug ${tagSlug} not found, languageCode: ${languageCode}`,
    );
  }

  return tag;
}

export async function queryApps({
  slug,
  services,
  languageCode,
  offset = 0,
  filter,
}: {
  services: Services;
  slug: string;
  languageCode: string;
  experiments: Experiments;
  offset?: number;
  filter?: QueryAppsFilter;
}): Promise<QueryAppsResponse> {
  const tags = await services.queryTag({
    slugs: [slug],
    languageCode,
    isHidden: false,
  });
  const tag = tags.findBySlug(slug);
  if (tag.id === '') {
    throw new Error(
      `Sub Category with slug ${slug} not found, languageCode: ${languageCode}`,
    );
  }

  const tagId = tag.id;

  const { appIds, hasMoreApps, totalApps } =
    await services.appMarketClientAggregator.enhancedSearchApps({
      sortTypes: [SortType.DEALER],
      paging: {
        limit: APPS_LIMIT,
        offset,
      },
      filter: { ...filter, tagId },
    });

  const appsData = await services.getAppsByAppIds({
    appIds,
    status: 'PUBLISHED',
  });
  const apps = appsData.toJSON();

  return {
    appGroup: {
      type: AppGroupType.COLLECTION,
      tagSlug: slug,
      apps: apps as AppGroupSection['apps'],
      title: '',
      totalAppsCount: 0,
    },
    paging: {
      total: totalApps,
      hasNext: hasMoreApps,
    },
  };
}
